import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCw, Trash2, MessageSquare, Users, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { isUserAdmin } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';

interface ChatMessage {
  id: string;
  name: string;
  message: string;
  created_at: string;
}

interface Viewer {
  id: string;
  name: string;
  joined_at: string;
}

function Avatar({ name }: { name: string }) {
  return (
    <div className="w-8 h-8 rounded-full bg-success-10 text-success-60 flex items-center justify-center text-xs font-bold flex-shrink-0 uppercase">
      {name.charAt(0)}
    </div>
  );
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function LiveChatAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState<'messages' | 'attendees'>('messages');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auth guard
  useEffect(() => {
    if (!user) { navigate('/si/login', { replace: true }); return; }
    isUserAdmin(user.id).then(admin => {
      if (!admin) { navigate('/si/login', { replace: true }); return; }
      setChecking(false);
    });
  }, [user, navigate]);

  const fetchMessages = async () => {
    const { data } = await supabase
      .from('live_chat_messages' as never)
      .select('*')
      .order('created_at', { ascending: true }) as { data: ChatMessage[] | null };
    if (data) setMessages(data);
  };

  const fetchViewers = async () => {
    const { data } = await supabase
      .from('live_viewers' as never)
      .select('*')
      .order('joined_at', { ascending: true }) as { data: Viewer[] | null };
    if (data) setViewers(data);
  };

  const refresh = async () => {
    setLoading(true);
    await Promise.all([fetchMessages(), fetchViewers()]);
    setLoading(false);
  };

  useEffect(() => {
    if (checking) return;
    refresh();

    // Realtime subscriptions
    const msgSub = supabase
      .channel('live_chat_messages_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_chat_messages' }, () => fetchMessages())
      .subscribe();

    const viewerSub = supabase
      .channel('live_viewers_admin')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_viewers' }, () => fetchViewers())
      .subscribe();

    return () => {
      supabase.removeChannel(msgSub);
      supabase.removeChannel(viewerSub);
    };
  }, [checking]);

  // Auto-scroll messages
  useEffect(() => {
    if (tab === 'messages') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, tab]);

  const deleteMessage = async (id: string) => {
    await supabase.from('live_chat_messages' as never).delete().eq('id' as never, id);
    setMessages(prev => prev.filter(m => m.id !== id));
  };

  const clearAllMessages = async () => {
    if (!confirm('Delete all messages? This cannot be undone.')) return;
    await supabase.from('live_chat_messages' as never).delete().neq('id' as never, '00000000-0000-0000-0000-000000000000');
    setMessages([]);
  };

  const clearAllViewers = async () => {
    if (!confirm('Clear the attendee list? This cannot be undone.')) return;
    await supabase.from('live_viewers' as never).delete().neq('id' as never, '00000000-0000-0000-0000-000000000000');
    setViewers([]);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-5 font-urbanist text-gray-80">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-80">Live Chat Admin</h1>
            <p className="text-sm text-gray-50 mt-0.5">Real-time viewer messages and attendees</p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-20 rounded-xl text-sm font-medium text-gray-60 hover:text-gray-80 hover:border-gray-30 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
          <div className="flex border-b border-gray-20">
            <button
              onClick={() => setTab('messages')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors ${tab === 'messages' ? 'text-gray-80 border-b-2 border-gray-80' : 'text-gray-50 hover:text-gray-70'}`}
            >
              <MessageSquare className="w-4 h-4" />
              Messages
              {messages.length > 0 && (
                <span className="bg-gray-80 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {messages.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setTab('attendees')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors ${tab === 'attendees' ? 'text-gray-80 border-b-2 border-gray-80' : 'text-gray-50 hover:text-gray-70'}`}
            >
              <Users className="w-4 h-4" />
              Attendees
              {viewers.length > 0 && (
                <span className="bg-success-40 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {viewers.length}
                </span>
              )}
            </button>
          </div>

          {/* Messages tab */}
          {tab === 'messages' && (
            <div>
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-10 bg-brand-5">
                <p className="text-xs text-gray-50">{messages.length} message{messages.length !== 1 ? 's' : ''}</p>
                {messages.length > 0 && (
                  <button
                    onClick={clearAllMessages}
                    className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-medium transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clear All
                  </button>
                )}
              </div>

              <div className="divide-y divide-gray-10 max-h-[28rem] overflow-y-auto">
                {messages.length === 0 ? (
                  <div className="py-16 text-center">
                    <MessageSquare className="w-8 h-8 text-gray-30 mx-auto mb-3" />
                    <p className="text-sm text-gray-40">No messages yet</p>
                    <p className="text-xs text-gray-30 mt-1">Questions from viewers will appear here in real time</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 px-6 py-4 hover:bg-brand-5 group transition-colors"
                    >
                      <Avatar name={msg.name} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-80">{msg.name}</span>
                          <span className="text-xs text-gray-40">{formatTime(msg.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-60 leading-relaxed">{msg.message}</p>
                      </div>
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                        title="Delete message"
                      >
                        <X className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </motion.div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          {/* Attendees tab */}
          {tab === 'attendees' && (
            <div>
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-10 bg-brand-5">
                <p className="text-xs text-gray-50">{viewers.length} attendee{viewers.length !== 1 ? 's' : ''}</p>
                {viewers.length > 0 && (
                  <button
                    onClick={clearAllViewers}
                    className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 font-medium transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clear List
                  </button>
                )}
              </div>

              <div className="divide-y divide-gray-10 max-h-[28rem] overflow-y-auto">
                {viewers.length === 0 ? (
                  <div className="py-16 text-center">
                    <Users className="w-8 h-8 text-gray-30 mx-auto mb-3" />
                    <p className="text-sm text-gray-40">No attendees yet</p>
                    <p className="text-xs text-gray-30 mt-1">People who register on the walkthrough page will appear here</p>
                  </div>
                ) : (
                  viewers.map(viewer => (
                    <motion.div
                      key={viewer.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 px-6 py-4"
                    >
                      <Avatar name={viewer.name} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-80">{viewer.name}</p>
                        <p className="text-xs text-gray-40 mt-0.5">Joined {formatDateTime(viewer.joined_at)}</p>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-40 text-center">
          Updates in real time. Use Clear buttons to reset between events.
        </p>
      </div>
    </div>
  );
}
